import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { Toast } from "../../components/Toast";

type ColorFormat = "hex" | "rgb" | "hsl";

type ColorState = {
  hex: string;
  rgb: string;
  hsl: string;
};

const isValidHex = (hex: string): boolean => {
  return /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
};

const isValidRgb = (rgb: string): boolean => {
  return /^\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*$/.test(rgb);
};

const isValidHsl = (hsl: string): boolean => {
  return /^\s*\d{1,3}\s*,\s*\d{1,3}%?\s*,\s*\d{1,3}%?\s*$/.test(hsl);
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const cleanHex = hex.replace("#", "");
  let fullHex = cleanHex;

  if (cleanHex.length === 3) {
    fullHex = cleanHex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  const num = parseInt(fullHex, 16);
  if (isNaN(num)) return null;

  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const rgbToHsl = (
  r: number,
  g: number,
  b: number,
): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const hslToRgb = (
  h: number,
  s: number,
  l: number,
): { r: number; g: number; b: number } | null => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};

const normalizeHex = (hex: string): string => {
  const clean = hex.replace("#", "");
  if (clean.length === 3) {
    return (
      "#" +
      clean
        .split("")
        .map((c) => c + c)
        .join("")
        .toUpperCase()
    );
  }
  return "#" + clean.toUpperCase();
};

const normalizeRgb = (rgb: string): string => {
  const parts = rgb.split(",").map((p) => parseInt(p.trim(), 10));
  return `${parts[0]}, ${parts[1]}, ${parts[2]}`;
};

const normalizeHsl = (hsl: string): string => {
  const parts = hsl.split(",").map((p) => p.trim());
  const h = parseInt(parts[0], 10);
  const s = parts[1].replace("%", "");
  const l = parts[2].replace("%", "");
  return `${h}, ${s}%, ${l}%`;
};

export function ColorConverter() {
  const { t } = useTranslation();
  const [colors, setColors] = useState<ColorState>({
    hex: "#22D3EE",
    rgb: "34, 211, 238",
    hsl: "187, 83%, 53%",
  });
  const [activeInput, setActiveInput] = useState<ColorFormat | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const getPreviewColor = useCallback((): string => {
    const hex = colors.hex;
    if (isValidHex(hex)) {
      return normalizeHex(hex);
    }
    return "#000000";
  }, [colors.hex]);

  const updateFromHex = useCallback((hex: string) => {
    if (!isValidHex(hex)) {
      setColors((prev) => ({ ...prev, hex }));
      return;
    }

    const normalizedHex = normalizeHex(hex);
    const rgb = hexToRgb(normalizedHex);
    if (!rgb) return;

    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

    setColors({
      hex: normalizedHex,
      rgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`,
      hsl: `${hsl.h}, ${hsl.s}%, ${hsl.l}%`,
    });
  }, []);

  const updateFromRgb = useCallback((rgb: string) => {
    if (!isValidRgb(rgb)) {
      setColors((prev) => ({ ...prev, rgb }));
      return;
    }

    const parts = rgb.split(",").map((p) => parseInt(p.trim(), 10));
    const [r, g, b] = parts;

    const hex = rgbToHex(r, g, b);
    const hsl = rgbToHsl(r, g, b);

    setColors({
      hex,
      rgb: normalizeRgb(rgb),
      hsl: `${hsl.h}, ${hsl.s}%, ${hsl.l}%`,
    });
  }, []);

  const updateFromHsl = useCallback((hsl: string) => {
    if (!isValidHsl(hsl)) {
      setColors((prev) => ({ ...prev, hsl }));
      return;
    }

    const parts = hsl.split(",").map((p) => p.trim());
    const h = parseInt(parts[0], 10);
    const s = parseInt(parts[1].replace("%", ""), 10);
    const l = parseInt(parts[2].replace("%", ""), 10);

    const rgb = hslToRgb(h, s, l);
    if (!rgb) return;

    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);

    setColors({
      hex,
      rgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`,
      hsl: normalizeHsl(hsl),
    });
  }, []);

  const handleInputChange = (format: ColorFormat, value: string) => {
    setActiveInput(format);
    switch (format) {
      case "hex":
        updateFromHex(value);
        break;
      case "rgb":
        updateFromRgb(value);
        break;
      case "hsl":
        updateFromHsl(value);
        break;
    }
  };

  const copyToClipboard = async (value: string, format: ColorFormat) => {
    try {
      await navigator.clipboard.writeText(value);
      setToastMessage(
        t("colorConverter.copied", { format: format.toUpperCase() }),
      );
    } catch {
      setToastMessage(t("colorConverter.copyFailed"));
    }
  };

  const inputConfigs: {
    format: ColorFormat;
    label: string;
    placeholder: string;
  }[] = [
    { format: "hex", label: t("colorConverter.hex"), placeholder: "#22D3EE" },
    {
      format: "rgb",
      label: t("colorConverter.rgb"),
      placeholder: "34, 211, 238",
    },
    {
      format: "hsl",
      label: t("colorConverter.hsl"),
      placeholder: "187, 83%, 53%",
    },
  ];

  return (
    <div className="color-converter">
      <div className="color-preview-section">
        <div
          className="color-preview-box"
          style={{ backgroundColor: getPreviewColor() }}
        />
        <p className="color-preview-label">{t("colorConverter.preview")}</p>
      </div>

      <div className="color-inputs-grid">
        {inputConfigs.map(({ format, label, placeholder }) => (
          <div key={format} className="color-input-card">
            <label className="color-input-label">{label}</label>
            <div className="color-input-wrapper">
              <input
                type="text"
                className="color-input"
                value={colors[format]}
                onChange={(e) => handleInputChange(format, e.target.value)}
                placeholder={placeholder}
              />
              <button
                type="button"
                className="color-copy-btn"
                onClick={() => copyToClipboard(colors[format], format)}
                aria-label={t("colorConverter.copy", {
                  format: format.toUpperCase(),
                })}
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
